// Sync engine F4-lite — estado inteiro por usuário, last-write-wins.
// Regras na inicialização (login/load):
//   1. nuvem vazia            → envia local (migração do localStorage)
//   2. local virgem           → aplica nuvem
//   3. ambos com dado         → o saved_at mais recente vence
// Depois: qualquer mudança local → push com debounce de 2s.

import type { AppState } from '@app-types/domain';
import { loadSavedAt } from '@core/persistence';
import { getState, replaceState, subscribe } from '@core/store';
import { supabase } from './supabase';
import { startEventSync, stopEventSync } from './sync-events';

export type SyncStatus = 'off' | 'syncing' | 'synced' | 'error';

export interface SyncSnapshot {
  status: SyncStatus;
  last_sync: string | null;
  detail?: string;
}

let snapshot: SyncSnapshot = { status: 'off', last_sync: null };
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

export function getSyncSnapshot(): SyncSnapshot {
  return snapshot;
}

export function subscribeSync(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setStatus(status: SyncStatus, detail?: string) {
  snapshot = {
    status,
    last_sync: status === 'synced' ? new Date().toISOString() : snapshot.last_sync,
    detail,
  };
  notify();
}

let currentUserId: string | null = null;
let unsubStore: (() => void) | null = null;
let pushTimer: ReturnType<typeof setTimeout> | null = null;
let applyingRemote = false;

function isVirgin(s: AppState): boolean {
  return s.contents.length === 0 && s.sessions.length === 0 && s.goals.length === 0;
}

async function push(userId: string): Promise<void> {
  const state = getState();
  const { error } = await supabase.from('user_states').upsert({
    user_id: userId,
    state,
    saved_at: new Date().toISOString(),
    version: 1,
  });
  if (error) throw new Error(error.message);
}

function schedulePush() {
  if (!currentUserId) return;
  if (pushTimer) clearTimeout(pushTimer);
  setStatus('syncing');
  pushTimer = setTimeout(async () => {
    if (!currentUserId) return;
    try {
      await push(currentUserId);
      setStatus('synced');
    } catch (e) {
      console.error('[sync] push falhou:', e);
      setStatus('error', e instanceof Error ? e.message : 'push falhou');
    }
  }, 2000);
}

function applyRemote(remoteState: AppState) {
  applyingRemote = true;
  try {
    replaceState(remoteState);
  } finally {
    // janela para o notify do store não disparar push do próprio apply
    setTimeout(() => {
      applyingRemote = false;
    }, 500);
  }
}

export async function startSync(userId: string): Promise<void> {
  if (currentUserId === userId && unsubStore) return; // já ativo
  stopSyncInternal();
  currentUserId = userId;
  setStatus('syncing');

  try {
    const { data, error } = await supabase
      .from('user_states')
      .select('state, saved_at')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) throw new Error(error.message);

    const local = getState();

    if (!data) {
      // primeira vez deste usuário: migra o local pra nuvem
      await push(userId);
      setStatus('synced', 'dados locais enviados pra nuvem');
    } else if (isVirgin(local)) {
      applyRemote(data.state as AppState);
      setStatus('synced', 'dados da nuvem aplicados');
    } else {
      const remoteAt = new Date(data.saved_at as string).getTime();
      const localAt = loadSavedAt() ? new Date(loadSavedAt() as string).getTime() : 0;
      if (remoteAt > localAt) {
        applyRemote(data.state as AppState);
        setStatus('synced', 'nuvem era mais recente');
      } else {
        await push(userId);
        setStatus('synced', 'local era mais recente');
      }
    }
  } catch (e) {
    console.error('[sync] init falhou:', e);
    setStatus('error', e instanceof Error ? e.message : 'sync falhou');
  }

  // observa mudanças locais → push com debounce
  unsubStore = subscribe(() => {
    if (applyingRemote) return;
    schedulePush();
  });

  // pipeline de eventos em paralelo (tabela append-only)
  startEventSync(userId);
}

function stopSyncInternal() {
  if (unsubStore) {
    unsubStore();
    unsubStore = null;
  }
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  currentUserId = null;
}

export function stopSync(): void {
  stopSyncInternal();
  stopEventSync();
  setStatus('off');
}

/** push imediato (botão nas Configurações) */
export async function forceSyncNow(): Promise<void> {
  if (!currentUserId) return;
  if (pushTimer) clearTimeout(pushTimer);
  setStatus('syncing');
  try {
    await push(currentUserId);
    setStatus('synced');
  } catch (e) {
    setStatus('error', e instanceof Error ? e.message : 'push falhou');
  }
}
